import { navigate, withPrefix } from 'gatsby';
import getLocation from './getLocation';

let observer = null;

export const MapCleanup = () => {
    observer.disconnect();
};

export const GenerateMap = (props) => {
    /**
     * Define constants used throughout script
     */
    const searchResultClass = 'storerocket-result';
    const addressSelector = '[class^="storerocket-"][class$="-address"]';
    const starsSelector = 'storerocket-result-stars';
    const star =
        '<svg viewBox="0 0 576 512" width="100" title="star"><path d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z" /></svg>';

    /**
     * Initialize the StoreRocket widget and setup mutation observer
     */
    const initializeMap = () => {
        const config = {
            selector: `#${props.mapRef.id}`,
            account: 'aKpDDRnpdR',
            theme: 'light'
        };

        if (props && props.location) {
            config.location = props.location;
        }

        // Override centerMap function
        window.StoreRocket.centerMap = function () {};
        window.StoreRocket.init(config);

        // Observer setup
        observer = new MutationObserver(function (mutations) {
            mutations.forEach(function (mutation) {
                // Check for added nodes
                mutation.addedNodes.forEach(function (node) {
                    if (
                        node.tagName &&
                        node.tagName === 'DIV' &&
                        node.getAttribute('class') === searchResultClass
                    ) {
                        // @TODO: only do this on the listing page
                        if (!node || node.dataset.modified) return;
                        node.dataset.modified = true;
                        updateStoreRocketListing(
                            node,
                            '.storerocket-result-button-1074'
                        );
                    }

                    // Check for overlay
                    if (
                        node.parentNode &&
                        node.parentNode.classList &&
                        node.parentNode.classList.contains(
                            'storerocket-overlay-details-name'
                        )
                    ) {
                        updateStoreRocketListing(
                            node.parentNode.parentNode,
                            '.storerocket-overlay-details-custom-ctas-item'
                        );
                    }
                });
            });
        });

        // Start observing
        observer.observe(document.getElementById(props.mapRef.id), {
            childList: true,
            subtree: true
        });
    };

    /**
     * Takes a StoreRocket button element and returns the associated Location slug provided
     * the Location has the 'View Details' call to action button defined with the slug assigned
     * as the 'href'. StoreRocket treats the slug as a URL, so 'http://' is prepended and '/'
     * is appended to the slug. This function will find the slug, remove the prefix/suffix, and
     * return the valid slug.
     * @param {Element} button The StoreRocket result button element.
     * @return {string} The corresponding slug for the listed location.
     */
    const getLocationSlugFromButton = (button) => {
        if (button) {
            return button
                .getAttribute('href')
                .replace(/^https?:\/\//, '')
                .replace(/\//g, '');
        } else {
            console.log(
                "'View Details' call to action not found for location!"
            );
            return null;
        }
    };

    /**
     * Takes a StoreRocket search result element, injects star ratings
     * and adds a new click event listener to the View Details button.
     * @param {Element} element The StoreRocket result DOM element
     */
    const updateStoreRocketListing = (element, buttonSelector) => {
        // The buttonSelector refers to the "View Details" call to action button.
        const button = element.querySelector(buttonSelector);

        // Get slug
        const slug = getLocationSlugFromButton(button);
        if (!slug) return;

        // Modify filters
        modifyFilters(element);

        // Update ratings
        injectRatings(element, slug);

        // Add click event to button
        button.slug = slug;
        button.href = withPrefix(
            `${process.env.BASE_WEB_PATH}locations/${slug}`
        );
        button.addEventListener('click', viewDetailsHandler);
    };

    /**
     * Pulls delivery and price filters out into separate rows.
     * @param {Element} element The StoreRocket result 'div' element.
     */
    const modifyFilters = (element) => {
        let overlay = false;
        let filtersSelector = '.storerocket-result-item-filter';
        if (element.classList.contains('storerocket-overlay-details-content')) {
            overlay = true;
            filtersSelector = '.storerocket-overlay-filters-item';
        }

        const filters = element.querySelectorAll(filtersSelector);
        if (!filters) return;

        /* Target and move Delivery filter */
        moveFilter(filters, overlay, 'Delivery');

        /* Target and move Price filter */
        moveFilter(filters, overlay, 'Price');
    };

    /**
     * Finds Delivery and Price filter items within list of provided
     * filters and moves them out and above the other filter items.
     * @param {*} filters
     * @param {string} targetName
     * @returns
     */
    const moveFilter = (filters, overlay, targetName) => {
        const targetFilter = Array.from(filters).find((filter) => {
            return filter.textContent.includes(targetName);
        });

        if (!targetFilter) {
            return;
        }

        targetFilter.className = 'filter';

        // Add span around value, remove colon
        if (targetFilter.textContent.includes(':')) {
            const targetContent = targetFilter.textContent.trim().split(':');

            // Generate label
            const labelSpan = document.createElement('span');
            labelSpan.className = 'filter-name';
            labelSpan.innerText = targetContent[0].trim();

            // Generate value
            const valueSpan = document.createElement('span');
            valueSpan.className = 'filter-value';
            valueSpan.innerText = targetContent[1].trim();

            // Clear and append
            targetFilter.innerHTML = '';
            targetFilter.appendChild(labelSpan);
            targetFilter.appendChild(valueSpan);
        } else {
            let nameSelector = '.storerocket-result-item-filter-name';

            if (overlay) {
                nameSelector = '.storerocket-overlay-filters-item';
                const labelSpan = document.createElement('span');
                labelSpan.className = 'filter-name';
                labelSpan.innerText = targetFilter.textContent.trim();
                targetFilter.innerHTML = '';
                targetFilter.appendChild(labelSpan);
            } else {
                targetFilter.querySelector(nameSelector).className =
                    'filter-name';
            }
        }

        const parent = targetFilter.parentNode;
        const wrapper = document.createElement('div');
        const slug = targetName.toLowerCase();
        const targetClass = `${slug}-filter`;
        // Prevent duplicates from rendering in the overlay
        let existingTarget = parent.parentNode.querySelector('.' + targetClass);
        if (existingTarget) {
            existingTarget.remove();
        }
        wrapper.className = targetClass;
        wrapper.appendChild(targetFilter);
        parent.parentNode.insertBefore(wrapper, parent);
    };

    /**
     * Event handler for View Details buttons
     * @param {*} event
     */
    const viewDetailsHandler = (event) => {
        event.stopPropagation();
        event.preventDefault();
        navigate(
            `${process.env.BASE_WEB_PATH}locations/${event.currentTarget.slug}`
        );
    };

    /**
     * Takes location slug, gets location data, generates star ratings,
     * and updates search results.
     * @param {Element} element The StoreRocket result 'div' element.
     * @param {string} slug The corresponding slug for the listed location.
     */
    const injectRatings = (element, slug) => {
        // Remove any existing stars
        if (element.querySelector(`.${starsSelector}`)) {
            element.querySelector(`.${starsSelector}`).remove();
        }

        getLocation(slug)
            .then(function (response) {
                if (
                    !response ||
                    !response.review_data ||
                    !response.review_data.rating
                )
                    return;

                const rating = response.review_data.rating;
                if (!rating) return;

                const stars = generateStars(rating);
                element
                    .querySelector(addressSelector)
                    .parentNode.insertBefore(
                        stars,
                        element.querySelector(addressSelector)
                    );
            })
            .catch(function (error) {
                console.log(error);
            });
    };

    /**
     * Generate HTML for star ratings
     * @param {number} count Star rating value out of 5
     * @returns {string}
     */
    const generateStars = (count) => {
        const resultStars = document.createElement('div');
        resultStars.classList.add(starsSelector);
        const starsWrapper = document.createElement('div');
        starsWrapper.classList.add('stars');
        starsWrapper.innerHTML = star + star + star + star + star;
        const percentage = (100 / 5) * (5 - count);
        starsWrapper.innerHTML +=
            ' <div class="cover" style="width: ' + percentage + '%"></div>';
        resultStars.appendChild(starsWrapper);
        return resultStars;
    };

    /**
     * Initialize map
     */
    initializeMap();
};
